/**
 * Scheduled Emails Panel — Workspace Settings tab
 *
 * Lists the user's scheduled mass-email batches (pending, sending, complete,
 * failed, cancelled). Cancel button only shown when the batch is still in
 * its cancellation window (enforced server-side too).
 */
import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { CalendarClock, Loader2, RefreshCw, Trash2, Users, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { useApi } from '../../hooks/useApi';


const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-900',
  sending: 'bg-blue-100 text-blue-900',
  complete: 'bg-emerald-100 text-emerald-900',
  failed: 'bg-red-100 text-red-900',
  cancelled: 'bg-zinc-100 text-zinc-700',
};


function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}


export default function ScheduledEmailsPanel() {
  const api = useApi();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/gmail/mass/scheduled');
      setRows(res.batches || []);
    } catch (e) {
      toast.error(e?.message || 'Failed to load scheduled batches');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const cancel = async (id) => {
    if (!window.confirm('Cancel this scheduled batch? Recipients will not be sent anything.')) return;
    try {
      await api.delete(`/gmail/mass/scheduled/${id}`);
      toast.success('Batch cancelled');
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || 'Cancel failed');
      toast.error(msg);
    }
  };

  return (
    <Card className="rounded-sm border" data-testid="scheduled-emails-panel">
      <CardContent className="p-5 md:p-6 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="rounded-sm p-2 bg-[#B89B5E]/10 text-[#B89B5E]">
              <CalendarClock className="w-5 h-5" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">
                Scheduled Emails
              </div>
              <div className="font-serif text-2xl md:text-3xl leading-none">
                Pending & past batches
              </div>
              <div className="mt-2 text-xs text-muted-foreground max-w-md">
                Scheduled mass-email batches run automatically at their send time.
                Cancel any time up to 5 minutes before send.
              </div>
            </div>
          </div>
          <Button
            variant="outline" size="sm" onClick={load}
            className="rounded-sm" data-testid="scheduled-emails-refresh"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="text-xs text-muted-foreground border border-dashed rounded-sm p-6 text-center">
            No scheduled batches yet. Use "Schedule" in the Mass Email composer to stage one.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="scheduled-emails-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b">
                  <th className="py-2 pr-3">Send at</th>
                  <th className="py-2 pr-3">Subject</th>
                  <th className="py-2 pr-3">Recipients</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Sent / Failed</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((b) => (
                  <tr key={b.id} className="border-b last:border-b-0 align-top">
                    <td className="py-2.5 pr-3">
                      <div className="flex items-center gap-1 text-xs">
                        <Clock className="w-3 h-3 text-muted-foreground" />
                        {fmtDate(b.send_at)}
                      </div>
                      {b.timezone_label && (
                        <div className="text-[10px] text-muted-foreground">{b.timezone_label}</div>
                      )}
                    </td>
                    <td className="py-2.5 pr-3">
                      <div className="font-medium max-w-[260px] truncate">{b.subject_template}</div>
                      {b.opportunity_id && (
                        <div className="text-[10px] text-muted-foreground font-mono">opp · {b.opportunity_id.slice(0, 8)}</div>
                      )}
                    </td>
                    <td className="py-2.5 pr-3">
                      <div className="flex items-center gap-1 text-xs">
                        <Users className="w-3 h-3 text-muted-foreground" />
                        {b.recipient_count}
                      </div>
                      {b.recipient_preview && b.recipient_preview.length > 0 && (
                        <div className="text-[10px] text-muted-foreground truncate max-w-[200px]">
                          {b.recipient_preview.join(', ')}
                          {b.recipient_count > b.recipient_preview.length ? '…' : ''}
                        </div>
                      )}
                    </td>
                    <td className="py-2.5 pr-3">
                      <Badge className={`rounded-sm hover:${STATUS_COLORS[b.status] || ''} ${STATUS_COLORS[b.status] || ''}`}>
                        {b.status}
                      </Badge>
                      {b.status === 'failed' && b.failure_reason && (
                        <div className="text-[10px] text-red-700 mt-0.5">{b.failure_reason}</div>
                      )}
                    </td>
                    <td className="py-2.5 pr-3 text-xs">
                      {b.sent_count !== undefined
                        ? `${b.sent_count} / ${b.failed_count || 0}`
                        : '—'}
                    </td>
                    <td className="py-2.5 text-right">
                      {b.cancellable && (
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => cancel(b.id)}
                          className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`scheduled-emails-cancel-${b.id}`}
                          title="Cancel this batch"
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1" /> Cancel
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
